import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

/**
 * Shared placeholders + a hardened <img> for signature / scanned-image fields.
 *
 * Why this exists: physical/Image.js already had an onError -> fallbackSrc
 * mechanism, but a repo-wide grep showed NO caller ever passed `fallbackSrc`,
 * so on error it set src to `undefined` and the browser drew a broken-image
 * icon anyway. The signature images on the physical exam form didn't even go
 * through that component - they were raw <img> tags with no handling at all.
 *
 * The placeholders are inline SVG data URIs on purpose:
 *   - no extra network request, so they cannot themselves 404 and re-trigger
 *     onError (which is how you get an infinite error loop);
 *   - react-to-print renders into a detached document where relative asset
 *     URLs are unreliable - a data URI always resolves.
 */

const svgToDataUri = svg =>
    `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg.replace(/\s+/g, ' ').trim())}`;

/**
 * Deliberately NOT a squiggle. These render onto a printed regulatory document
 * (Edonorsoft SOP Form No. 01.01.01); a generic signature-looking graphic could be
 * read by an auditor as evidence that a signature was actually captured. The
 * marker states plainly that nothing is on file.
 *
 * viewBox is 5:1 to match the 60x12 `signature` class in PhysicalView, so it
 * scales down without distorting.
 */
export const SIGNATURE_PLACEHOLDER = svgToDataUri(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 60" width="300" height="60">
  <rect x="2" y="2" width="296" height="56" fill="#f5f5f5"
        stroke="#9e9e9e" stroke-width="3" stroke-dasharray="10 6"/>
  <text x="150" y="38" text-anchor="middle" fill="#5f5f5f"
        font-family="Helvetica, Arial, sans-serif" font-size="22" font-weight="bold"
        letter-spacing="0.5">NO SIGNATURE ON FILE</text>
</svg>
`);

export const IMAGE_PLACEHOLDER = svgToDataUri(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 240" width="320" height="240">
  <rect x="2" y="2" width="316" height="236" fill="#f5f5f5"
        stroke="#9e9e9e" stroke-width="3" stroke-dasharray="10 6"/>
  <text x="160" y="122" text-anchor="middle" fill="#5f5f5f"
        font-family="Helvetica, Arial, sans-serif" font-size="20" font-weight="bold">IMAGE NOT ON FILE</text>
</svg>
`);

// '' and '   ' matter here: an empty string src resolves against the page URL,
// so the browser fetches the HTML document, fails to decode it as an image and
// draws a broken icon. null/undefined make React drop the attribute entirely
// and no error event ever fires - which is why the old onError-only approach
// never caught a *missing* signature, only a *failing* one.
const isBlank = value =>
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim() === '');

// Default parameters rather than defaultProps: this project is on React 18.3,
// which warns that defaultProps on function components is going away.
const SafeImage = ({ src, fallbackSrc = IMAGE_PLACEHOLDER, alt = '', ...rest }) => {
    const [failed, setFailed] = useState(false);

    // Reset on src change. The old class component seeded state in the
    // constructor only, so once `errored` flipped true it stayed true - moving
    // from /physicals/6/show to /physicals/7/show without unmounting kept
    // showing the previous record's failed image.
    useEffect(() => {
        setFailed(false);
    }, [src]);

    const usingFallback = isBlank(src) || failed;
    const resolved = usingFallback ? fallbackSrc : src;

    return (
        <img
            src={resolved}
            alt={usingFallback ? `${alt || 'Image'} - not on file` : alt}
            data-placeholder={usingFallback ? 'true' : undefined}
            // Not wired while already showing the fallback, so a fallback that
            // somehow fails can't loop.
            onError={usingFallback ? undefined : () => setFailed(true)}
            {...rest}
        />
    );
};

SafeImage.propTypes = {
    src: PropTypes.string,
    fallbackSrc: PropTypes.string,
    alt: PropTypes.string,
};

export default SafeImage;
